import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Link2, Unlink, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  getPositionBindings, getBindableSlots, bindSlotToPosition, unbindSlotFromPosition,
} from '../api';
import {
  Position, PositionBinding, HeadcountSlot, HC_STATUS_LABELS, HC_EMPLOYMENT_LABELS, User,
} from '../types';
import { fmtDateTime } from './positionShared';

interface Props {
  position: Position;
  currentUser: User;
  /** 绑定关系变化后通知详情页刷新聚合列 */
  onChanged: () => void;
  /** 跳转编制管理页并定位到该编制编号 */
  onOpenSlot?: (code: string) => void;
}

/** 编制状态徽标，配色与编制管理页保持一致 */
const SLOT_STATUS_COLORS: Record<string, string> = {
  AVAILABLE: 'bg-gray-100 text-gray-500',
  RECRUITING: 'bg-emerald-50 text-emerald-600',
  RESERVED: 'bg-amber-50 text-amber-600',
  OCCUPIED: 'bg-blue-50 text-blue-600',
  CLOSED: 'bg-gray-100 text-gray-400',
};

function SlotStatus({ status }: { status: string }) {
  return (
    <span className={clsx('px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap',
      SLOT_STATUS_COLORS[status] || SLOT_STATUS_COLORS.AVAILABLE)}>
      {HC_STATUS_LABELS[status as keyof typeof HC_STATUS_LABELS] || status}
    </span>
  );
}

export default function PositionBindingTab({ position, currentUser, onChanged, onOpenSlot }: Props) {
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';

  const [bindings, setBindings] = useState<PositionBinding[]>([]);
  const [bindable, setBindable] = useState<HeadcountSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBindable, setShowBindable] = useState(false);
  const [busySlot, setBusySlot] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bs, avail] = await Promise.all([
        getPositionBindings(position.id),
        // 无部门实体归属时后端一定返回空数组，此处仍照常请求以保持单一数据源
        getBindableSlots(position.id).catch(() => []),
      ]);
      setBindings(Array.isArray(bs) ? bs : []);
      setBindable(Array.isArray(avail) ? avail : []);
    } catch {
      toast.error('加载编制绑定失败');
    } finally {
      setLoading(false);
    }
  }, [position.id]);

  useEffect(() => { load(); }, [load]);

  const handleBind = async (slot: HeadcountSlot) => {
    setBusySlot(slot.id);
    try {
      await bindSlotToPosition(position.id, slot.id);
      toast.success(`编制 ${slot.code} 已绑定`);
      await load();
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || '绑定失败');
    } finally {
      setBusySlot('');
    }
  };

  const handleUnbind = async (b: PositionBinding) => {
    const reason = window.prompt(`解除编制 ${b.slot.code} 的绑定？可填写原因（选填）`, '');
    if (reason === null) return;
    setBusySlot(b.headcount_slot_id);
    try {
      await unbindSlotFromPosition(position.id, b.headcount_slot_id, reason || undefined);
      toast.success(`编制 ${b.slot.code} 已解绑`);
      await load();
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || '解绑失败');
    } finally {
      setBusySlot('');
    }
  };

  const SlotCode = ({ code }: { code: string }) => (
    onOpenSlot ? (
      <button onClick={() => onOpenSlot(code)}
        className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1">
        {code}<ExternalLink size={11} />
      </button>
    ) : <span className="font-medium text-gray-700">{code}</span>
  );

  const occupied = bindings.filter(b => b.slot.status === 'OCCUPIED').length;

  return (
    <div className="space-y-4">
      {/* 部门实体缺失时绑定必然被后端 ValidationError 拒绝，前置提示避免用户反复试 */}
      {!position.org_unit_id && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>该岗位尚未归属到组织架构中的部门实体，无法绑定编制。请先在「编辑岗位」中重新选择所属部门。</span>
        </div>
      )}

      {/* 已绑定编制 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Link2 size={15} className="text-primary-500" />
            已绑定编制
            <span className="text-xs font-normal text-gray-400">
              共 {bindings.length} 个 · 已占用 {occupied} 个
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={load} className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50">
              <RefreshCw size={13} className={clsx(loading && 'animate-spin')} />
            </button>
            {isHr && (
              <button onClick={() => setShowBindable(v => !v)} disabled={!position.org_unit_id}
                className="h-8 px-3 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-40 flex items-center gap-1">
                <Plus size={12} />绑定编制
              </button>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 bg-gray-50">
              <th className="text-left font-medium px-5 py-2.5">编制编号</th>
              <th className="text-left font-medium px-3 py-2.5">状态</th>
              <th className="text-left font-medium px-3 py-2.5">用工类型</th>
              <th className="text-left font-medium px-3 py-2.5">绑定时间</th>
              <th className="text-left font-medium px-3 py-2.5">绑定人</th>
              <th className="text-left font-medium px-3 py-2.5">备注</th>
              <th className="text-right font-medium px-5 py-2.5">操作</th>
            </tr>
          </thead>
          <tbody>
            {bindings.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-gray-400">
                {loading ? '加载中…' : '尚未绑定任何编制'}
              </td></tr>
            ) : bindings.map(b => (
              <tr key={b.id} className="border-t border-gray-100">
                <td className="px-5 py-3 text-xs"><SlotCode code={b.slot.code} /></td>
                <td className="px-3 py-3"><SlotStatus status={b.slot.status} /></td>
                <td className="px-3 py-3 text-xs text-gray-600">
                  {HC_EMPLOYMENT_LABELS[b.slot.employment_type as keyof typeof HC_EMPLOYMENT_LABELS] || '—'}
                </td>
                <td className="px-3 py-3 text-xs text-gray-500">{fmtDateTime(b.bound_at)}</td>
                <td className="px-3 py-3 text-xs text-gray-600">{b.bound_by_name || '—'}</td>
                <td className="px-3 py-3 text-xs text-gray-500 max-w-[200px] truncate">{b.slot.notes || '—'}</td>
                <td className="px-5 py-3 text-right">
                  {isHr && (
                    // RESERVED / OCCUPIED 须先处理 Offer 才能解绑，此处直接禁用并说明原因
                    <button onClick={() => handleUnbind(b)}
                      disabled={busySlot === b.headcount_slot_id || b.slot.status === 'RESERVED' || b.slot.status === 'OCCUPIED'}
                      title={b.slot.status === 'RESERVED' ? '该编制已被 Offer 锁定，请先撤回 Offer'
                        : b.slot.status === 'OCCUPIED' ? '该编制已被占用，无法解绑' : ''}
                      className="text-xs text-red-500 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed inline-flex items-center gap-1">
                      <Unlink size={11} />解除绑定
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 可绑定编制 */}
      {showBindable && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">可绑定编制</h3>
            <button onClick={() => setShowBindable(false)} className="text-xs text-gray-400 hover:text-gray-600">收起</button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50">
                <th className="text-left font-medium px-5 py-2.5">编制编号</th>
                <th className="text-left font-medium px-3 py-2.5">状态</th>
                <th className="text-left font-medium px-3 py-2.5">对应岗位</th>
                <th className="text-left font-medium px-3 py-2.5">职级</th>
                <th className="text-left font-medium px-3 py-2.5">备注</th>
                <th className="text-right font-medium px-5 py-2.5">操作</th>
              </tr>
            </thead>
            <tbody>
              {bindable.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-400">
                  暂无可绑定的空闲编制
                </td></tr>
              ) : bindable.map(s => (
                <tr key={s.id} className="border-t border-gray-100">
                  <td className="px-5 py-3 text-xs"><SlotCode code={s.code} /></td>
                  <td className="px-3 py-3"><SlotStatus status={s.status} /></td>
                  <td className="px-3 py-3 text-xs text-gray-600">{s.position_name || '—'}</td>
                  <td className="px-3 py-3 text-xs text-gray-600">{s.job_level || '—'}</td>
                  <td className="px-3 py-3 text-xs text-gray-500 max-w-[200px] truncate">{s.notes || '—'}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => handleBind(s)} disabled={busySlot === s.id || !isHr}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:text-gray-300">
                      绑定
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-2.5 border-t border-gray-100 text-[11px] text-gray-400">
            仅显示与该岗位同部门（{position.department || '未分配'}）的可用编制
          </div>
        </div>
      )}
    </div>
  );
}
